import { showApiError } from "@/lib/toast";
import { updateContentLink } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { LoadingSmall } from "@/components/LoadingSmall";
import { Input } from "@/components/ui/input";

interface Props {
  contentGuid: string;
  link: string;
  onSuccess: () => void;
  onOpenChange: (open: boolean) => void;
}

const formSchema = z.object({
  link: z.string().min(1),
});

type FormSchemaType = z.infer<typeof formSchema>;

export const ContentLinkUpdateSheet: React.FC<Props> = ({ contentGuid, link, onSuccess, onOpenChange }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      link,
    },
  });

  const renameMutation = useMutation({
    mutationFn: (data: FormSchemaType) => {
      return updateContentLink({
        firmGuid: String(activeFirm?.firmGuid),
        contentGuid: contentGuid,
        link: data.link,
      });
    },
    onSuccess: () => {
      onOpenChange(false);
      onSuccess();
    },
    onError: showApiError,
  });

  const onSubmit = (data: FormSchemaType) => {
    renameMutation.mutate(data);
  };

  return (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("common.updateLink")}</SheetTitle>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="link"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("content.link")}</FormLabel>
                    <FormControl>
                      <div>
                        <Input value={field.value} onChange={field.onChange} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={renameMutation.isPending}>
                {t("common.save")} {renameMutation.isPending && <LoadingSmall />}
              </Button>
            </form>
          </Form>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};
